import type { ApiConfig, ApiError, ApiInstance, ApiRequestConfig, ApiRequestOptions, HttpMethod } from '@/types/api';
import type { $Fetch, NitroFetchRequest } from 'nitropack';

/**
 * Create an API Error
 */
function createApiError(
  message: string,
  status?: number,
  statusText?: string,
  data?: unknown,
  config?: ApiRequestConfig
): ApiError {
  const error = new Error(message) as ApiError;
  error.status = status;
  error.statusText = statusText;
  error.data = data;
  error.config = config;
  return error;
}

function getAccessToken() {
  // Dynamically get current auth token from Pinia store or fallback to cookie
  let token: string | undefined | null = null;

  const accessToken = useCookie(useRuntimeConfig().public.auth?.provider?.token?.cookieName || 'accessToken');
  if (accessToken.value) {
    token = accessToken.value;
  }

  // Pinia store not available, fallback to cookie
  const { token: authToken } = useAuth();
  if (!token && authToken.value) {
    token = authToken.value;
  }

  if (token) {
    return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }
  return undefined;
}

/**
 * Create API Client
 */
function createApiClient(config: ApiConfig, $api: $Fetch<unknown, NitroFetchRequest>): ApiInstance {
  const { baseURL, timeout = 30000, headers: defaultHeaders = {}, withCredentials = false } = config;

  // Define HTTP methods as constants
  const HTTP_METHODS = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    PATCH: 'PATCH',
    DELETE: 'DELETE',
  } as const satisfies Record<string, HttpMethod>;

  async function makeRequest<T>(requestConfig: ApiRequestConfig): Promise<T> {
    const {
      url,
      method = 'GET',
      data,
      params,
      headers = {},
      timeout: requestTimeout = timeout,
      retries = 3,
      retryDelay = 1000,
      ...options
    } = requestConfig;

    // Build full URL with proper slash handling
    const fullUrl = url.startsWith('http') ? url : `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;

    const token = getAccessToken();
    if (token) {
      defaultHeaders['Authorization'] = token;
    }

    // Prepare $fetch options
    const fetchOptions: Record<string, unknown> = {
      method,
      headers: {
        ...defaultHeaders,
        ...headers,
      },
      timeout: requestTimeout,
      retry: retries,
      retryDelay,
      ...options,
      $fetch: $api,
    };

    // Add query parameters
    if (params) {
      fetchOptions.query = params;
    }

    // Add body data for POST, PUT, PATCH
    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      fetchOptions.body = data;

      // Remove Content-Type for FormData (let $fetch handle it)
      if (data instanceof FormData && fetchOptions.headers) {
        const headers = fetchOptions.headers as Record<string, string>;
        delete headers['Content-Type'];
      }
    }

    // Set credentials
    if (withCredentials) {
      fetchOptions.credentials = 'include';
    }

    try {
      const result = await $fetch<T>(fullUrl, fetchOptions);
      return result as T;
    } catch (error: unknown) {
      // Handle $fetch errors
      if (error && typeof error === 'object') {
        const fetchError = error as {
          status?: number;
          statusText?: string;
          statusMessage?: string;
          message?: string;
          data?: unknown;
          response?: {
            status?: number;
            statusText?: string;
            _data?: unknown;
          };
        };

        // Try multiple sources for error message, prioritizing API response message
        const responseData = fetchError.data || fetchError.response?._data;
        const apiMessage =
          responseData && typeof responseData === 'object' && 'message' in responseData
            ? (responseData as { message: string }).message
            : null;

        const errorMessage =
          apiMessage ||
          fetchError.message ||
          fetchError.statusMessage ||
          fetchError.statusText ||
          fetchError.response?.statusText ||
          'HTTP Error';

        const status = fetchError.status || fetchError.response?.status;
        const statusText = fetchError.statusText || fetchError.response?.statusText;

        throw createApiError(errorMessage, status, statusText, responseData, requestConfig);
      }

      if (error instanceof Error) {
        // Handle timeout and abort errors
        if (error.name === 'TimeoutError' || error.name === 'AbortError') {
          throw createApiError('Request timeout', 0, 'Timeout', null, requestConfig);
        }

        // Handle network errors
        if (error.message.includes('fetch')) {
          throw createApiError('Network error', 0, 'Network Error', error.message, requestConfig);
        }

        throw error;
      }

      throw createApiError('Unknown error', 0, 'Unknown', error, requestConfig);
    }
  }

  return {
    async get<T = unknown>(url: string, options?: ApiRequestOptions): Promise<T> {
      return makeRequest<T>({
        url,
        method: HTTP_METHODS.GET,
        ...options,
      } as ApiRequestConfig);
    },

    async post<T = unknown>(url: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
      return makeRequest<T>({
        url,
        method: HTTP_METHODS.POST,
        data,
        ...options,
      } as ApiRequestConfig);
    },

    async put<T = unknown>(url: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
      return makeRequest<T>({
        url,
        method: HTTP_METHODS.PUT,
        data,
        ...options,
      } as ApiRequestConfig);
    },

    async patch<T = unknown>(url: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
      return makeRequest<T>({
        url,
        method: HTTP_METHODS.PATCH,
        data,
        ...options,
      } as ApiRequestConfig);
    },

    async delete<T = unknown>(url: string, options?: ApiRequestOptions): Promise<T> {
      return makeRequest<T>({
        url,
        method: HTTP_METHODS.DELETE,
        ...options,
      } as ApiRequestConfig);
    },

    async request<T = unknown>(config: ApiRequestConfig): Promise<T> {
      return makeRequest<T>(config);
    },
  };
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();

  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
  };

  // Determine baseURL based on environment and config
  const baseURL = config.public?.useProxyPath ? '/api' : (config.public?.auth?.baseURL as string);

  const apiConfig: ApiConfig = {
    baseURL,
    timeout: 30000,
    headers,
    withCredentials: true,
  };

  const $api = $fetch.create({
    baseURL,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
    },
    credentials: 'include',
    timeout: 30000,
    onRequest({ options }) {
      const token = getAccessToken();
      if (token) {
        options.headers.set('Authorization', token);
      }
    },
    async onResponseError({ response, options }) {
      // You can add global error handling here, e.g. show a toast/snackbar
      // For now, just throw a standardized error);
      throw createApiError(
        response.statusText || 'HTTP Error',
        response.status,
        response.statusText,
        response._data,
        options as ApiRequestConfig
      );
    },
  });

  // Create API instance
  const $apiClient = createApiClient(apiConfig, $api);

  // Provide API instance globally
  nuxtApp.provide('apiClient', $apiClient);
});
